/**
 * useAddToLeads — adds the sender of an inbox email to the World DB "Leads"
 * list, mirroring the classic SmartInbox "Add to Leads" behaviour exactly.
 *
 * If a contact with the same lead_email already exists in `World/{uid}/db`,
 * its status is flipped to "Lead" (and any selected touchpoints are merged in).
 * Otherwise a fresh contact doc is created with status "Lead". The Leads tab in
 * the World DB reads the same collection, so the contact shows up there and can
 * be managed further from then on.
 *
 * Optional touchpoints (interest level, meetings, sales steps, …) can be passed
 * in from the modal; custom touchpoints are loaded from RTDB the same way the
 * classic inbox does.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  getFirestore,
  collection,
  doc,
  getDocs,
  query,
  where,
  updateDoc,
  setDoc,
} from 'firebase/firestore';
import { ref, onValue } from 'firebase/database';
import { database } from '../../../firebaseConfig';
import { extractEmail, parseAddress } from '../utils/emailParsing';
import {
  extractEmailDomain,
  isBusinessDomain,
  computeDomainDedup,
} from '../../../shared/dedupNormalize';

// Touchpoint groups shown in the "Add to Leads" modal. Kept identical to the
// classic SmartInbox so both inboxes tag leads with the same set of signals.
export const TOUCHPOINT_GROUPS = {
  interest: {
    label: 'Interest Level',
    icon: 'fa-thermometer-half',
    items: {
      general_interest: { label: 'General Interest', icon: 'fa-thumbs-up', points: 2 },
      highly_interested: { label: 'Highly Interested', icon: 'fa-fire', points: 3 },
      not_now_but_friendly: { label: 'Not Now, But Friendly', icon: 'fa-clock', points: 1 },
      requested_info: { label: 'Requested Info', icon: 'fa-info-circle', points: 2 },
    },
  },
  meetings: {
    label: 'Meetings & Calls',
    icon: 'fa-calendar-check',
    items: {
      zoom_call_scheduled: { label: 'Zoom Call Scheduled', icon: 'fa-video', points: 3 },
      meet_in_person: { label: 'Meet In Person', icon: 'fa-users', hasCount: true, points: 4 },
      online_meeting: { label: 'Online Meeting', icon: 'fa-video', hasCount: true, points: 3 },
      phone_call: { label: 'Phone Call', icon: 'fa-phone-alt', hasCount: true, points: 2 },
    },
  },
  sales: {
    label: 'Sales Process',
    icon: 'fa-chart-line',
    items: {
      demo_given: { label: 'Demo Given', icon: 'fa-play-circle', hasCount: true, points: 3 },
      proposal_sent: { label: 'Proposal Sent', icon: 'fa-file-alt', hasCount: true, points: 3 },
      contract_sent: { label: 'Contract Sent', icon: 'fa-file-contract', points: 3 },
      interested_in_groupchat: { label: 'Interested in Groupchat', icon: 'fa-comments', points: 2 },
    },
  },
  linkedin: {
    label: 'LinkedIn',
    icon: 'fa-linkedin',
    isBrand: true,
    items: {
      linkedin_connected: { label: 'Connected', icon: 'fa-linkedin', isBrand: true, points: 1 },
      linkedin_message: { label: 'Message Sent', icon: 'fa-linkedin', isBrand: true, hasCount: true, points: 1 },
    },
  },
  content: {
    label: 'Content & Events',
    icon: 'fa-calendar-alt',
    items: {
      loom_video: { label: 'Loom Video Sent', icon: 'fa-video', hasCount: true, points: 2 },
      webinar_attended: { label: 'Webinar Attended', icon: 'fa-desktop', hasCount: true, points: 2 },
    },
  },
};

export const pickBusinessName = (data, fallback) => {
  let dmName = '';
  try {
    if (data.lead_decision_makers) {
      const dms = typeof data.lead_decision_makers === 'string'
        ? JSON.parse(data.lead_decision_makers)
        : data.lead_decision_makers;
      if (Array.isArray(dms) && dms.length > 0 && dms[0].name) dmName = dms[0].name;
    }
  } catch (e) { /* ignore malformed decision-maker blobs */ }
  return data.business_name || dmName || data.lead_firstname || fallback || 'Unknown';
};

// Normalized sender address from an inbox email's `from` (which may be a plain
// string or a structured { name, address } object). Always lowercased so it
// matches the stored `lead_email` / `lead_domain_dedup` fields.
const senderAddress = (from) => (
  extractEmail(from) || parseAddress(from).email || ''
).toLowerCase();

// Parses the (string-or-array) decision-maker blob and returns its length.
const countDecisionMakers = (data) => {
  try {
    const raw = data.lead_decision_makers;
    if (!raw) return 0;
    const dms = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return Array.isArray(dms) ? dms.length : 0;
  } catch (e) {
    return 0;
  }
};

// Best-effort company-size string across the various enrichment shapes the
// crawler / spy pipeline persists onto a contact.
const extractCompanySize = (data) => (
  data.companySizeEstimate
  || data.company_size
  || data.potential_analysis?.companySizeEstimate
  || data.researchIntelligence?.companySizeEstimate
  || data.spyIntelligence?.companySizeEstimate
  || ''
);

// True if the contact carries any AI-research / enrichment signal.
const hasResearchSignal = (data) => !!(
  data.spy_last_updated
  || data.potential_analysis
  || data.researchIntelligence
  || data.website_analysis_at
);

// True if a contact is "enriched" — i.e. it holds the kind of CRM data we want
// a lead to inherit. A bare inbox stub (just a name + email) is NOT rich.
const hasRichData = (data) => !!(
  data.lead_description
  || countDecisionMakers(data) > 0
  || hasResearchSignal(data)
  || extractCompanySize(data)
);

// Rough "how much do we know about this contact" score — used to pick the most
// useful contact when several share the same company domain.
const contactRichness = (data) => {
  let score = 0;
  if (data.lead_description) score += 1;
  score += countDecisionMakers(data);
  if (extractCompanySize(data)) score += 1;
  if (hasResearchSignal(data)) score += 2;
  if (data.lead_website) score += 1;
  return score;
};

/**
 * Finds the contact in `World/{uid}/db` that a given inbox sender belongs to.
 *
 * Resolution:
 *   1. Exact `lead_email` match — the sender is already a known contact.
 *      • If that contact is already enriched, it wins (it's the real record).
 *      • If it's only a bare stub (e.g. an auto-created reply contact) AND a
 *        richer contact shares the company domain, we link to the richer
 *        company contact instead and mark the stub for consolidation.
 *   2. Otherwise a company-domain match via `lead_domain_dedup` — the sender
 *      writes from the same company as an existing contact (free-mail domains
 *      are ignored so we never link two unrelated gmail.com people). When
 *      several contacts share the domain we pick the richest one.
 *
 * Returns `{ matchType: null }` when nothing matches, so the caller can fall
 * back to creating a fresh, standalone lead.
 *
 * @returns {Promise<{ matchType: 'email'|'domain'|null, docId?: string, data?: object, domain?: string, sparseContact?: object }>}
 */
export const resolveExistingContact = async (db, userId, emailAddress) => {
  const dbCollectionRef = collection(db, `World/${userId}/db`);

  const emailSnap = await getDocs(
    query(dbCollectionRef, where('lead_email', '==', emailAddress)),
  );
  const emailMatch = emailSnap.empty
    ? null
    : { docId: emailSnap.docs[0].id, data: emailSnap.docs[0].data() };

  // Richest business-domain contact (free-mail domains excluded).
  let domainMatch = null;
  let domain = extractEmailDomain(emailAddress);
  if (domain && isBusinessDomain(domain)) {
    const domainSnap = await getDocs(
      query(dbCollectionRef, where('lead_domain_dedup', '==', domain)),
    );
    if (!domainSnap.empty) {
      let best = domainSnap.docs[0];
      let bestScore = contactRichness(best.data());
      domainSnap.docs.slice(1).forEach((d) => {
        const score = contactRichness(d.data());
        if (score > bestScore) {
          best = d;
          bestScore = score;
        }
      });
      domainMatch = { docId: best.id, data: best.data(), score: bestScore };
    }
  } else {
    domain = '';
  }

  if (emailMatch) {
    if (hasRichData(emailMatch.data)) {
      return { matchType: 'email', docId: emailMatch.docId, data: emailMatch.data };
    }
    // Bare stub → prefer a richer company contact on the same domain.
    if (
      domainMatch
      && domainMatch.docId !== emailMatch.docId
      && domainMatch.score > contactRichness(emailMatch.data)
    ) {
      return {
        matchType: 'domain',
        docId: domainMatch.docId,
        data: domainMatch.data,
        domain,
        sparseContact: { docId: emailMatch.docId, data: emailMatch.data },
      };
    }
    return { matchType: 'email', docId: emailMatch.docId, data: emailMatch.data };
  }

  if (domainMatch) {
    return { matchType: 'domain', docId: domainMatch.docId, data: domainMatch.data, domain };
  }

  return { matchType: null };
};

// Shapes a resolved match into the light-weight preview the modal renders so
// the user can see which existing contact (and which of its DB fields) a lead
// will inherit before confirming.
const buildLinkedPreview = (match) => {
  if (!match || !match.matchType) return { matchType: null };
  const data = match.data || {};
  return {
    matchType: match.matchType,
    domain: match.domain || '',
    contactId: match.docId,
    businessName: pickBusinessName(data),
    alreadyLead: data.lead_status === 'Lead',
    consolidatesContact: !!match.sparseContact,
    fields: {
      description: !!data.lead_description,
      decisionMakers: countDecisionMakers(data),
      companySize: extractCompanySize(data),
      research: hasResearchSignal(data),
      website: !!data.lead_website,
    },
  };
};

export default function useAddToLeads(userId) {
  const [addingToLeads, setAddingToLeads] = useState(false);
  const [customTouchpoints, setCustomTouchpoints] = useState([]);
  const db = getFirestore();

  // Load user-defined custom touchpoints (same RTDB path as the classic inbox).
  useEffect(() => {
    if (!userId) return undefined;
    const customRef = ref(database, `World/${userId}/settings/customTouchpoints`);
    const unsubscribe = onValue(customRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = [];
        // RTDB forEach cancels iteration if the callback returns a truthy value;
        // arr.push returns the new length, so use a block body (returns undefined).
        snapshot.forEach((child) => {
          data.push({ id: child.key, ...child.val() });
        });
        setCustomTouchpoints(data);
      } else {
        setCustomTouchpoints([]);
      }
    });
    return () => unsubscribe();
  }, [userId]);

  /**
   * Looks up (without writing anything) which existing DB contact an inbox
   * sender belongs to, so the modal can preview the link before confirming.
   *
   * @param {object} email an inbox email doc (uses `email.from`)
   * @returns {Promise<object>} a preview object (see buildLinkedPreview)
   */
  const findLinkedContact = useCallback(async (email) => {
    if (!email || !userId) return { matchType: null };
    const emailAddress = senderAddress(email.from);
    if (!emailAddress) return { matchType: null };
    try {
      const match = await resolveExistingContact(db, userId, emailAddress);
      return buildLinkedPreview(match);
    } catch (err) {
      console.warn('[useAddToLeads] preview lookup failed:', err?.message);
      return { matchType: null };
    }
  }, [userId, db]);

  /**
   * Adds the sender of the given email to the Leads list.
   *
   * The sender is first resolved against the existing DB (exact email, then
   * company domain). If a contact is found we simply flip THAT document to
   * status "Lead" — because the Leads tab and the World DB read the same
   * collection, the lead then automatically shares (and stays in sync with) the
   * contact's description, decision makers, company size and research. Nothing
   * is copied. Only when no contact matches do we create a fresh standalone
   * lead, so the flow keeps working without any database history.
   *
   * @param {object} email an inbox email doc (uses `email.from`)
   * @param {object} touchpointsToAdd optional map of selected touchpoints
   * @returns {Promise<{ success: boolean, message: string }>}
   */
  const addToLeads = useCallback(async (email, touchpointsToAdd = {}) => {
    if (addingToLeads || !email || !userId) {
      return { success: false, message: 'Nothing to add.' };
    }

    const emailAddress = senderAddress(email.from);
    if (!emailAddress) {
      return { success: false, message: 'No valid sender address found in this email.' };
    }

    setAddingToLeads(true);
    try {
      const match = await resolveExistingContact(db, userId, emailAddress);
      const now = Date.now();
      const touchpointCount = Object.keys(touchpointsToAdd).length;
      const touchpointSuffix = touchpointCount > 0
        ? ` with ${touchpointCount} touchpoint${touchpointCount > 1 ? 's' : ''}`
        : '';

      if (match.matchType === 'email' || match.matchType === 'domain') {
        // Existing contact → flip the same document to "Lead" and merge
        // touchpoints. Rich fields are left untouched so the lead inherits them.
        const contactData = match.data;
        const businessName = pickBusinessName(contactData);
        const stubTouchpoints = match.sparseContact?.data?.touchpoints || {};
        const mergedTouchpoints = {
          ...(contactData.touchpoints || {}),
          ...stubTouchpoints,
          ...touchpointsToAdd,
        };

        const patch = {
          lead_contacted: 'Lead',
          lead_status: 'Lead',
          lead_added_at: new Date().toISOString(),
          lead_since: now,
          updated_at: now,
          // A contact that was previously trashed/removed must resurface in the
          // Leads list — otherwise the "added" lead stays invisible.
          trashedAt: null,
          trashedBy: null,
          trashedByEmail: null,
          trashedByRole: null,
          removedAt: null,
          removedBy: null,
          removedByEmail: null,
          removedByRole: null,
          ...(Object.keys(mergedTouchpoints).length > 0 && { touchpoints: mergedTouchpoints }),
        };

        if (match.matchType === 'domain') {
          // Linked via company domain rather than an exact address. Record the
          // inbox address that actually reached out (without clobbering the
          // contact's primary email) so we don't lose who replied.
          patch.linked_from_inbox = true;
          const senderName = parseAddress(email.from).name || '';
          const existingEmail = (contactData.lead_email || '').toLowerCase();
          if (emailAddress && emailAddress !== existingEmail) {
            patch.inbox_sender_email = emailAddress;
            if (senderName) patch.inbox_sender_name = senderName;
          }
        }

        const contactRef = doc(db, `World/${userId}/db/${match.docId}`);
        await updateDoc(contactRef, patch);

        // A bare stub contact was superseded by the richer company contact →
        // demote it out of the Leads list (reversible, nothing deleted) so we
        // don't keep a duplicate lead for the same company.
        if (match.sparseContact && match.sparseContact.docId !== match.docId) {
          const stubRef = doc(db, `World/${userId}/db/${match.sparseContact.docId}`);
          await updateDoc(stubRef, {
            lead_status: 'Not Contacted',
            lead_contacted: 'Not Contacted',
            consolidated_into: match.docId,
            consolidated_at: now,
            updated_at: now,
          });
        }

        const via = match.matchType === 'domain' && match.domain
          ? ` (linked via ${match.domain})`
          : '';
        return {
          success: true,
          message: `"${businessName}" added to Leads${via}${touchpointSuffix}!`,
        };
      }

      // No existing contact → create a fresh, standalone Lead entry.
      const senderName = parseAddress(email.from).name || 'Unknown';
      const newLead = {
        lead_email: emailAddress,
        business_name: senderName,
        lead_contacted: 'Lead',
        lead_status: 'Lead',
        lead_added_at: new Date().toISOString(),
        lead_since: now,
        timestamp: new Date().toISOString(),
        created_at: now,
        updated_at: now,
        source: 'smart_inbox',
        has_email: true,
        ...(Object.keys(touchpointsToAdd).length > 0 && { touchpoints: touchpointsToAdd }),
      };
      // Keep the domain-dedup field in sync so a future contact/import from the
      // same company can still be linked to this lead.
      newLead.lead_domain_dedup = computeDomainDedup(newLead);

      const newContactRef = doc(collection(db, `World/${userId}/db`));
      await setDoc(newContactRef, newLead);

      return {
        success: true,
        message: `"${senderName}" added as new Lead${touchpointSuffix}!`,
      };
    } catch (err) {
      console.error('[useAddToLeads] add failed:', err);
      return { success: false, message: err?.message || 'Could not add contact to Leads.' };
    } finally {
      setAddingToLeads(false);
    }
  }, [addingToLeads, userId, db]);

  return { addingToLeads, addToLeads, findLinkedContact, customTouchpoints };
}
